import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UtensilsCrossed, Music, Briefcase } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import type { Theme } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { logger } from '../../lib/logger';
import { setThemePreselectVisible } from '../../lib/themePreselect';
import { HERO_MEDIA } from '../../config/heroMedia';
import { THEME_SELECTOR_MEDIA } from '../../config/themeSelectorMedia';
import type { ThemeSelectorCardKey } from '../../config/themeSelectorMedia';
import '../../components/UI/SpotlightCard.css';
import './ThemeSelectorMotion.css';

const dotIndexStyle = (i: number) => ({ '--ts-i': i }) as React.CSSProperties;

const CardMedia: React.FC<{ card: ThemeSelectorCardKey; overlayClassName: string }> = ({
  card,
  overlayClassName,
}) => {
  const media = THEME_SELECTOR_MEDIA.cards[card];
  return (
    <div className="ts-media" aria-hidden="true">
      <img
        src={media.src}
        srcSet={media.srcSet}
        sizes={THEME_SELECTOR_MEDIA.sizes}
        width={media.width}
        height={media.height}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover brightness-50"
      />
      <div className={`absolute inset-0 bg-gradient-to-b ${overlayClassName}`} />
    </div>
  );
};

const ProfessionalParticles = lazy(() =>
  import('../effects/ProfessionalParticles').then((m) => ({
    default: m.ProfessionalParticles,
  })),
);

interface ThemeSelectorModalProps {
  forceShow?: boolean;
}

export const ThemeSelectorModal: React.FC<ThemeSelectorModalProps> = ({ forceShow = false }) => {
  const { setTheme, setColorScheme, setSurface } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const { i18n } = useTranslation();
  const [show, setShow] = useState<boolean>(() => {
    if (forceShow) return true;
    const isBot = /bot|crawler|spider|google|bing|yandex/i.test(navigator.userAgent);
    if (isBot) return false;
    const pathname = window.location.pathname;
    const hasSkinInUrl = /\/(resto|office|nightclub)\/?/.test(pathname);
    if (hasSkinInUrl) return false;
    const isHomepage = pathname === '/' || /^\/[a-z]{2}(-[A-Z]{2})?\/?$/i.test(pathname);
    return isHomepage;
  });

  useEffect(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const skinFromUrl = pathParts.find(part => ['resto', 'office', 'nightclub'].includes(part)) as Theme | undefined;
    
    if (skinFromUrl) {
      setTheme(skinFromUrl);
      setShow(false);
    }
  }, [setTheme]);

  useEffect(() => {
    if (forceShow) {
      setShow(true);
      return;
    }
    
    const isBot = /bot|crawler|spider|google|bing|yandex/i.test(navigator.userAgent);
    const isHomepage = window.location.pathname === '/' || 
                       window.location.pathname.match(/^\/[a-z]{2}(-[A-Z]{2})?\/?$/i);
    const hasSkinInUrl = /\/(resto|office|nightclub)\/?/.test(window.location.pathname);
    
    if (!isHomepage || hasSkinInUrl || isBot) {
      setShow(false);
      return;
    }
    
    setShow(!user);
  }, [user, forceShow]);

  // Empêcher le scroll du body quand la modale est ouverte (SAUF sur mobile où on permet le scroll)
  useEffect(() => {
    if (show) {
      // Sur mobile, permettre le scroll dans la modal elle-même
      const isMobile = window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (!isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        // Sur mobile, empêcher le scroll du body mais permettre le scroll dans la modal
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    };
  }, [show]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show]);

  // Hide Onyx FAB while Hospitality / Entertainment / Office picker is up (mobile + desktop)
  useLayoutEffect(() => {
    const visible = Boolean(show && !user);
    setThemePreselectVisible(visible);
    return () => setThemePreselectVisible(false);
  }, [show, user]);

  // Particles after LCP — never on mobile / reduced-motion (CWV)
  const [particlesReady, setParticlesReady] = useState(false);
  useEffect(() => {
    if (!show || user) {
      setParticlesReady(false);
      return;
    }
    const allowParticles = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      return window.matchMedia(`(min-width: ${HERO_MEDIA.desktopMinWidthPx}px)`).matches;
    };
    if (!allowParticles()) return;

    const enable = () => setParticlesReady(true);
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(enable, { timeout: 6000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(enable, 3000);
    return () => clearTimeout(t);
  }, [show, user]);

  const handleThemeSelection = (selectedTheme: Theme) => {
    setShow(false);
    setThemePreselectVisible(false);
    
    const currentLang = i18n.language || 'en';
    const skinPath = `/${currentLang}/${selectedTheme}`;
    
    logger.log(`⚡ Modal Switch RAPIDE vers ${selectedTheme}`);
    
    // 🚀 OPTIMISÉ : Changement instantané sans rechargement
    setTheme(selectedTheme);
    
    // Orientation univers → mode couleur :
    // Hospitality (resto) + Entertainment (nightclub) → Moon ; Office → Sun
    if (selectedTheme === 'nightclub' || selectedTheme === 'resto') {
      setColorScheme('dark');
      setSurface('default');
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('theme-black');
      logger.log(
        selectedTheme === 'resto'
          ? '🌙 Hospitality: moon (dark)'
          : '🌙 Entertainment: moon (dark)',
      );
    } else if (selectedTheme === 'office') {
      setColorScheme('light');
      setSurface('default');
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.remove('theme-black');
      logger.log('☀️ Office: sun (light)');
    }
    
    // 🔥 Juste changer l'URL sans déclencher popstate (évite double détection)
    window.history.pushState({}, '', skinPath);
  };

  const restoRef = useRef<HTMLDivElement>(null);
  const nightclubRef = useRef<HTMLDivElement>(null);
  const officeRef = useRef<HTMLDivElement>(null);

  const handleSpotlight = useCallback((e: React.MouseEvent<HTMLDivElement>, ref: React.RefObject<HTMLDivElement | null>, color: string) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    ref.current.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
    ref.current.style.setProperty('--spotlight-color', color);
  }, []);

  const handleSpotlightLeave = useCallback((ref: React.RefObject<HTMLDivElement | null>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    ref.current.style.setProperty('--mouse-x', `${rect.width / 2}px`);
    ref.current.style.setProperty('--mouse-y', `${rect.height / 2}px`);
  }, []);

  if (!show || user) return null;

  return (
      <div
        className="fixed inset-0 z-[60] bg-black overflow-y-auto" 
        data-testid="theme-selector-modal"
        data-nosnippet="true"
        aria-hidden="true"
        role="presentation"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {particlesReady && (
          <div className="fixed inset-0 z-0 pointer-events-none">
            <Suspense fallback={null}>
              <ProfessionalParticles forceBright={true} />
            </Suspense>
          </div>
        )}

        <div className="relative z-10 flex flex-col items-center text-white md:h-screen md:max-h-screen w-full overflow-y-auto md:overflow-hidden pt-12 md:pt-10">
          <div className="w-full md:flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-3 lg:gap-4 p-3 md:p-3 lg:p-4 max-w-[1600px] mx-auto md:min-h-0">
            {/* Card Hospitality */}
            <div 
              ref={restoRef}
              className="card-spotlight ts-card group cursor-pointer !rounded-xl md:!rounded-2xl !bg-gradient-to-b from-[#0d0d0d] via-[#141210] to-[#0d0d0d] text-white flex flex-col !p-0"
              style={{ borderColor: 'rgba(249, 115, 22, 0.3)' }}
              onClick={() => handleThemeSelection('resto')}
              onMouseMove={(e) => handleSpotlight(e, restoRef, 'rgba(249, 115, 22, 0.25)')}
              onMouseLeave={() => handleSpotlightLeave(restoRef)}
            >
              <CardMedia card="resto" overlayClassName="from-[#0d0d0d]/70 via-[#141210]/70 to-[#0d0d0d]/70" />
              <div className="ts-accent absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500 opacity-80 group-hover:opacity-100 transition-opacity z-[2]" />
              
              <div className="relative z-[1] flex flex-col h-full p-4 sm:p-5 md:p-4 lg:p-5 xl:p-8">
                <div className="flex items-center gap-2 mb-3 md:mb-4 lg:mb-5">
                  <UtensilsCrossed className="ts-float w-5 h-5 md:w-6 md:h-6 text-orange-400" />
                  <span className="text-orange-400 font-bold text-xs md:text-sm tracking-wider">KOMBY</span>
                </div>
                
                <div className="flex-1 flex flex-col min-h-0">
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-extrabold mb-2 md:mb-3 leading-[1.1]">
                    <span className="text-orange-400">Komby</span><br />
                    <span className="text-white">{t('themeSelector.resto.name')}</span>
                  </h2>
                  <p className="text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 text-white/45 leading-relaxed">
                    {t('themeSelector.resto.subtitle')}
                  </p>
                  
                  <ul className="space-y-1.5 md:space-y-2 text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 overflow-y-auto flex-1 min-h-0">
                    {['booking', 'review', 'up', 'capsule', 'analytics'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="ts-dot w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0 mt-1" style={dotIndexStyle(i)} />
                        <span className="text-white/65 leading-snug">{t(`themeSelector.resto.features.${key}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-auto pt-2">
                  <div className="ts-cta w-full py-2.5 md:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-black rounded-xl text-xs md:text-sm font-semibold hover:brightness-110 transition-all duration-300 shadow-lg shadow-orange-500/20 text-center">
                    {t('themeSelector.chooseTheme', 'Select')}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card Nightclub */}
            <div 
              ref={nightclubRef}
              className="card-spotlight ts-card ts-card--2 group cursor-pointer !rounded-xl md:!rounded-2xl !bg-gradient-to-b from-[#0a0a12] via-[#10101a] to-[#0a0a12] text-white flex flex-col !p-0"
              style={{ borderColor: 'rgba(139, 92, 246, 0.3)' }}
              onClick={() => handleThemeSelection('nightclub')}
              onMouseMove={(e) => handleSpotlight(e, nightclubRef, 'rgba(139, 92, 246, 0.3)')}
              onMouseLeave={() => handleSpotlightLeave(nightclubRef)}
            >
              <CardMedia card="nightclub" overlayClassName="from-[#0a0a12]/70 via-[#10101a]/70 to-[#0a0a12]/70" />
              <div className="ts-accent absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 opacity-80 group-hover:opacity-100 transition-opacity z-[2]" />
              
              <div className="relative z-[1] flex flex-col h-full p-4 sm:p-5 md:p-4 lg:p-5 xl:p-8">
                <div className="flex items-center gap-2 mb-3 md:mb-4 lg:mb-5">
                  <Music className="ts-float w-5 h-5 md:w-6 md:h-6 text-purple-400" />
                  <span className="text-purple-400 font-bold text-xs md:text-sm tracking-wider">KOMBY</span>
                </div>
                
                <div className="flex-1 flex flex-col min-h-0">
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-extrabold mb-2 md:mb-3 leading-[1.1]">
                    <span className="text-purple-400">Komby</span><br />
                    <span className="text-white">{t('themeSelector.nightclub.name')}</span>
                  </h2>
                  <p className="text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 text-white/45 leading-relaxed">
                    {t('themeSelector.nightclub.subtitle')}
                  </p>
                  
                  <ul className="space-y-1.5 md:space-y-2 text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 overflow-y-auto flex-1 min-h-0">
                    {['booking', 'up', 'buzz', 'review', 'dj', 'capsule', 'analytics', 'events', 'people'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="ts-dot w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0 mt-1" style={dotIndexStyle(i)} />
                        <span className="text-white/65 leading-snug">{t(`themeSelector.nightclub.features.${key}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-auto pt-2">
                  <div className="ts-cta w-full py-2.5 md:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl text-xs md:text-sm font-semibold hover:brightness-110 transition-all duration-300 shadow-lg shadow-purple-500/20 text-center">
                    {t('themeSelector.chooseTheme', 'Select')}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card Office */}
            <div 
              ref={officeRef}
              className="card-spotlight ts-card ts-card--3 group cursor-pointer !rounded-xl md:!rounded-2xl !bg-gradient-to-b from-[#12110e] via-[#18160f] to-[#12110e] text-white flex flex-col !p-0"
              style={{ borderColor: 'rgba(252, 211, 77, 0.3)' }}
              onClick={() => handleThemeSelection('office')}
              onMouseMove={(e) => handleSpotlight(e, officeRef, 'rgba(252, 211, 77, 0.3)')}
              onMouseLeave={() => handleSpotlightLeave(officeRef)}
            >
              <CardMedia card="office" overlayClassName="from-[#12110e]/70 via-[#18160f]/70 to-[#12110e]/70" />
              <div className="ts-accent absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-amber-300 via-stone-400 to-amber-300 opacity-80 group-hover:opacity-100 transition-opacity z-[2]" />
              
              <div className="relative z-[1] flex flex-col h-full p-4 sm:p-5 md:p-4 lg:p-5 xl:p-8">
                <div className="flex items-center gap-2 mb-3 md:mb-4 lg:mb-5">
                  <Briefcase className="ts-float w-5 h-5 md:w-6 md:h-6 text-amber-300" />
                  <span className="text-amber-300 font-bold text-xs md:text-sm tracking-wider">KOMBY</span>
                </div>
                
                <div className="flex-1 flex flex-col min-h-0">
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-extrabold mb-2 md:mb-3 leading-[1.1]">
                    <span className="text-amber-300">Komby</span><br />
                    <span className="text-white">{t('office:shortName', 'Office')}</span>
                  </h2>
                  <p className="text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 text-white/45 leading-relaxed">
                    {t('themeSelector.office.subtitle')}
                  </p>
                  
                  <ul className="space-y-1.5 md:space-y-2 text-[11px] md:text-xs lg:text-sm mb-3 md:mb-4 overflow-y-auto flex-1 min-h-0">
                    {['booking', 'up', 'buzz', 'web', 'people', 'review', 'analytics'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="ts-dot w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0 mt-1" style={dotIndexStyle(i)} />
                        <span className="text-white/65 leading-snug">{t(`themeSelector.office.features.${key}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-auto pt-2">
                  <div className="ts-cta w-full py-2.5 md:py-3 bg-gradient-to-r from-amber-300 to-stone-400 text-black rounded-xl text-xs md:text-sm font-semibold hover:brightness-110 transition-all duration-300 shadow-lg shadow-amber-300/20 text-center">
                    {t('themeSelector.chooseTheme', 'Select')}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
  );
};